// tabs/VanzariTab.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const DateRangeInput = React.forwardRef(function DateRangeInput(
  { value, onClick, placeholder },
  ref
) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Interval de date"
      value={value}
      onClick={onClick}
      inputRef={ref}
      placeholder={placeholder}
      InputProps={{ readOnly: true }}
    />
  );
});

const startOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const QUICK_RANGE_PRESETS = [
  { label: "Astăzi", value: "today" },
  { label: "Ultimele 7 zile", value: "last7" },
  { label: "Ultimele 30 zile", value: "last30" },
  { label: "Luna curentă", value: "thisMonth" },
];

const TABLE_COLUMNS = [
  { id: "rec", label: "REC", align: "center" },
  { id: "nr_jurnal_special", label: "Nr. Jurnal Special", align: "center" },
  { id: "sku", label: "SKU", align: "center" },
  { id: "summary", label: "Summary", align: "center" },
  { id: "nume_si_prenume", label: "Nume și Prenume", align: "center" },
  { id: "data_intrare", label: "Data Intrare", align: "center" },
  { id: "pret_intrare", label: "Preț Intrare", align: "center" },
  { id: "data_vandut", label: "Data Vânzare", align: "center" },
  { id: "pret_de_vanzare_ron", label: "Preț Vânzare", align: "center" },
  { id: "profitRON", label: "Profit", align: "center" },
  { id: "tvaRON", label: "TVA", align: "center" },
  { id: "REZULTAT_FORMULA", label: "Impozit Profit", align: "center" },
  { id: "client", label: "Client", align: "center" },
  { id: "factura", label: "Factura", align: "center" },
  { id: "comanda", label: "Comanda", align: "center" },
  { id: "data_factura", label: "Date Factură", align: "center" },
  { id: "pret_luxura", label: "Preț Luxura", align: "center" },
];

const COLUMN_VALUE_GETTERS = {
  rec: (row) => (parseInt(row.rec, 10) === 1 ? 1 : 0),
  nr_jurnal_special: (row) => row.nr_jurnal_special || "",
  sku: (row) => row.sku || "",
  summary: (row) => row.summary || "",
  nume_si_prenume: (row) => row.nume_si_prenume || "",
  data_intrare: (row) =>
    row.data_intrare ? new Date(row.data_intrare).getTime() : null,
  pret_intrare: (row) =>
    row.pret_intrare !== null && row.pret_intrare !== undefined
      ? Number(row.pret_intrare)
      : null,
  data_vandut: (row) =>
    row.data_vandut ? new Date(row.data_vandut).getTime() : null,
  pret_de_vanzare_ron: (row) =>
    row.pret_de_vanzare_ron !== null && row.pret_de_vanzare_ron !== undefined
      ? Number(row.pret_de_vanzare_ron)
      : null,
  profitRON: (row) =>
    row.profitRON !== null && row.profitRON !== undefined
      ? Number(row.profitRON)
      : null,
  tvaRON: (row) =>
    row.tvaRON !== null && row.tvaRON !== undefined ? Number(row.tvaRON) : null,
  REZULTAT_FORMULA: (row) =>
    row.REZULTAT_FORMULA !== null && row.REZULTAT_FORMULA !== undefined
      ? Number(row.REZULTAT_FORMULA)
      : null,
  client: (row) => {
    const first = row.orderInfo?.first_name || "";
    const last = row.orderInfo?.last_name || "";
    const combined = `${first} ${last}`.trim();
    return combined || row.orderInfo?.billing?.company || "";
  },
  factura: (row) => row.orderInfo?._ywpi_invoice_formatted_number || "",
  comanda: (row) =>
    row.orderInfo?.ID !== undefined && row.orderInfo?.ID !== null
      ? row.orderInfo.ID.toString()
      : "",
  data_factura: (row) =>
    row.orderInfo?._ywpi_invoice_date
      ? new Date(row.orderInfo._ywpi_invoice_date).getTime()
      : null,
  pret_luxura: (row) =>
    row.orderInfo?.total !== null && row.orderInfo?.total !== undefined
      ? Number(row.orderInfo.total)
      : null,
};

function VanzariTab() {
  const [comparisonData, setComparisonData] = useState({
    inSalesNotInTasksAsSold: [],
    inTasksAsSoldNotInSales: [],
  });
  const [salesData, setSalesData] = useState([]);
  const [salesDataFiltered, setSalesDataFiltered] = useState([]);
  const [salesStartDate, setSalesStartDate] = useState(null);
  const [salesEndDate, setSalesEndDate] = useState(null);
  const generatedAt = useMemo(() => new Date().toLocaleDateString("ro-RO"), []);
  const filteredRangeLabel = useMemo(() => {
    if (!salesStartDate || !salesEndDate) {
      return "Toate datele";
    }
    const startLabel = salesStartDate.toLocaleDateString("ro-RO");
    const endLabel = salesEndDate.toLocaleDateString("ro-RO");
    return `${startLabel} - ${endLabel}`;
  }, [salesStartDate, salesEndDate]);
  const [selectedQuickRange, setSelectedQuickRange] = useState("");
  const [showRectificari, setShowRectificari] = useState(false);
  // Croscheck state
  const [selectedYear, setSelectedYear] = useState(2025);
  const [croscheckData, setCroscheckData] = useState({
    vandutInJiraNotInVanzari: [],
    vandutInVanzariNotInJira: [],
  });
  const [numarErori, setNumarErori] = useState(0);
  const [summaryTotals, setSummaryTotals] = useState({
    totalPretVanzare: 0,
    totalProfit: 0,
    totalTVA: 0,
    totalImpozit: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const integerCurrencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("ro-RO", {
        maximumFractionDigits: 0,
      }),
    []
  );

  const formatKpiValue = (value, withLei = true) => {
    const numericValue = Number(value) || 0;
    const formatted = integerCurrencyFormatter.format(Math.round(numericValue));
    return withLei ? `${formatted} lei` : formatted;
  };

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setSalesStartDate(start ? startOfDay(start) : null);
    setSalesEndDate(end ? endOfDay(end) : null);
    setSelectedQuickRange("");
  };

  const handleQuickRangeSelect = (presetValue) => {
    const today = new Date();
    let start = startOfDay(today);
    let end = endOfDay(today);

    switch (presetValue) {
      case "last7":
        start = startOfDay(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000));
        break;
      case "last30":
        start = startOfDay(
          new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)
        );
        break;
      case "thisMonth":
        start = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
        end = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      default:
        break;
    }

    setSalesStartDate(start);
    setSalesEndDate(end);
    setSelectedQuickRange(presetValue);
  };

  const handleClearRange = () => {
    setSalesStartDate(null);
    setSalesEndDate(null);
    setSelectedQuickRange("");
  };

  const isRangeSelected = Boolean(
    salesStartDate || salesEndDate || selectedQuickRange
  );

  const actionButtonStyles = {
    bgcolor: "rgba(255,255,255,0.18)",
    color: "#fff",
    textTransform: "none",
    borderRadius: 3,
    px: 2.5,
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.32)",
    },
  };

  const handleSort = (columnId) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.field === columnId) {
        return {
          field: columnId,
          direction: prevConfig.direction === "asc" ? "desc" : "asc",
        };
      }
      return { field: columnId, direction: "asc" };
    });
  };

  const sortedSalesData = useMemo(() => {
    if (!Array.isArray(salesDataFiltered)) {
      return [];
    }
    if (!sortConfig.field) {
      return salesDataFiltered;
    }
    const getter = COLUMN_VALUE_GETTERS[sortConfig.field];
    if (!getter) {
      return salesDataFiltered;
    }

    const dataCopy = [...salesDataFiltered];
    dataCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (valueA === null || valueA === undefined) {
        return valueB === null || valueB === undefined
          ? 0
          : sortConfig.direction === "asc"
            ? 1
            : -1;
      }
      if (valueB === null || valueB === undefined) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }

      const comparison = valueA
        .toString()
        .localeCompare(valueB.toString(), "ro", {
          numeric: true,
          sensitivity: "base",
        });

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    return dataCopy;
  }, [salesDataFiltered, sortConfig]);

  const tableRows = Array.isArray(sortedSalesData) ? sortedSalesData : [];
  // Fetch croscheck data: fetch when showRectificari is true or selectedYear changes
  useEffect(() => {
    const fetchCroscheckData = async () => {
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/vanzari-croscheck?anul=${selectedYear}`
        );
        setCroscheckData(response.data);
        const totalErrors =
          (response.data.vandutInJiraNotInVanzari?.length || 0) +
          (response.data.vandutInVanzariNotInJira?.length || 0);
        setNumarErori(totalErrors);
      } catch (error) {
        console.error("Eroare la încărcarea datelor de rectificare:", error);
      }
    };
    fetchCroscheckData();
  }, [showRectificari, selectedYear]);

  // Fetch initial croscheck data on mount for selectedYear
  useEffect(() => {
    const fetchInitialCroscheckData = async () => {
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/vanzari-croscheck?anul=${selectedYear}`
        );
        setCroscheckData(response.data);
        const totalErrors =
          (response.data.vandutInJiraNotInVanzari?.length || 0) +
          (response.data.vandutInVanzariNotInJira?.length || 0);
        setNumarErori(totalErrors);
      } catch (error) {
        console.error(
          "Eroare la încărcarea datelor inițiale de croscheck:",
          error
        );
      }
    };
    fetchInitialCroscheckData();
  }, []);

  // (Removed: Fetch initial croscheck data on mount for 2025)

  const totalVanzari = salesDataFiltered.length;

  const fetchSalesData = async () => {
    try {
      const startDateStr = salesStartDate
        ? salesStartDate.toISOString().split("T")[0]
        : "";
      const endDateStr = salesEndDate
        ? salesEndDate.toISOString().split("T")[0]
        : "";
      const url = `https://server-9p4hx.kinsta.app/api/tasks/vanzari-filtrate?de_la=${startDateStr}&pana_la=${endDateStr}`;
      const response = await axios.get(url);
      const payload = response.data;
      const rows = Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload)
          ? payload
          : [];
      setSalesData(rows);
      setSummaryTotals({
        totalPretVanzare: payload?.totals?.totalPretVanzare || 0,
        totalProfit: payload?.totals?.totalProfit || 0,
        totalTVA: payload?.totals?.totalTVA || 0,
        totalImpozit: payload?.totals?.totalImpozit || 0,
      });
    } catch (error) {
      console.error("Eroare la preluarea datelor filtrate de vânzări:", error);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [salesStartDate, salesEndDate]);

  useEffect(() => {
    const sorted = [...salesData].sort((a, b) => {
      const dateA = new Date(a.orderInfo?._ywpi_invoice_date || 0);
      const dateB = new Date(b.orderInfo?._ywpi_invoice_date || 0);
      return dateA - dateB;
    });
    setSalesDataFiltered(sorted);
  }, [salesData]);

  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        const startDateString = salesStartDate
          ? salesStartDate.toISOString()
          : "";
        const endDateString = salesEndDate ? salesEndDate.toISOString() : "";
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/jurnale/compare-skus?startDate=${startDateString}&endDate=${endDateString}`
        );
        setComparisonData(response.data);
      } catch (error) {
        console.error("Failed to fetch comparison data:", error);
      }
    };
    fetchComparisonData();
  }, [salesStartDate, salesEndDate]);

  const handleRecChangeForVanzari = async (issue_id, newRecValue) => {
    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/jurnale/update-rec",
        {
          issue_id,
          rec: newRecValue ? 1 : 0,
        }
      );
      if (response.status === 200) {
        const updated = salesDataFiltered.map((row) =>
          row.issue_id === issue_id ? { ...row, rec: newRecValue ? 1 : 0 } : row
        );
        setSalesDataFiltered(updated);
      }
    } catch (error) {
      console.error("Error updating rec value:", error);
    }
  };

  const exportToExcel = (data) => {
    const XLSX = require("xlsx");

    const dataCopy = Array.isArray(data) ? [...data] : [];

    // Sort data by invoice date ascending
    dataCopy.sort((a, b) => {
      const dateA = new Date(a.orderInfo?._ywpi_invoice_date || 0);
      const dateB = new Date(b.orderInfo?._ywpi_invoice_date || 0);
      return dateA - dateB;
    });

    const formatDate = (d) => {
      if (!d) return "—";
      const date = new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    const formatNumber = (n) =>
      n !== undefined && n !== null && !isNaN(Number(n))
        ? Number(n).toFixed(2)
        : "—";

    const rows = dataCopy.map((row) => {
      const clientName = row.orderInfo
        ? `${row.orderInfo.first_name || ""} ${row.orderInfo.last_name || ""}`.trim()
        : "—";
      const facturaNr =
        row.orderInfo?.["_ywpi_invoice_formatted_number"] || "—";
      const comandaId = row.orderInfo?.ID || "—";
      const dataFactura = formatDate(row.orderInfo?.["_ywpi_invoice_date"]);
      const pretLuxura = formatNumber(row.orderInfo?.total);

      return {
        REC: row.rec === 1 ? "✔️" : "",
        "Nr. Jurnal Special": row.nr_jurnal_special || "—",
        SKU: row.sku || "—",
        Summary: row.summary || "—",
        "Nume și Prenume": row.nume_si_prenume || "—",
        "Data Intrare": formatDate(row.data_intrare),
        "Preț Intrare": formatNumber(row.pret_intrare),
        "Data Vânzare": formatDate(row.data_vandut),
        "Preț Vânzare": formatNumber(row.pret_de_vanzare_ron),
        Client: clientName,
        Factura: facturaNr,
        Comanda: comandaId,
        "Data Factură": dataFactura,
        "Preț Luxura": pretLuxura,
        Profit: formatNumber(row.profitRON),
        TVA: formatNumber(row.tvaRON),
        "Impozit Profit": formatNumber(row.REZULTAT_FORMULA),
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Vanzari");
    XLSX.writeFile(wb, "Export_Vanzari.xlsx");
  };

  const exportToPDF = (data, meta = {}) => {
    const { jsPDF } = require("jspdf");
    require("jspdf-autotable");
    const doc = new jsPDF({ orientation: "landscape", format: "a3" });

    const dataCopy = Array.isArray(data) ? [...data] : [];

    // Sort data by invoice date before export
    dataCopy.sort((a, b) => {
      const dateA = new Date(a.orderInfo?._ywpi_invoice_date || 0);
      const dateB = new Date(b.orderInfo?._ywpi_invoice_date || 0);
      return dateA - dateB;
    });

    const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
    const formatNumber = (n) =>
      n !== undefined && n !== null && !isNaN(Number(n))
        ? Number(n).toFixed(2)
        : "—";

    const rows = [];

    dataCopy.forEach((row) => {
      const clientName = row.orderInfo
        ? `${row.orderInfo.first_name || ""} ${row.orderInfo.last_name || ""}`.trim()
        : "—";
      const facturaNr =
        row.orderInfo?.["_ywpi_invoice_formatted_number"] || "—";
      const comandaId = row.orderInfo?.ID || "—";
      const dataFactura = formatDate(row.orderInfo?.["_ywpi_invoice_date"]);
      const pretLuxura = formatNumber(row.orderInfo?.total);

      // Linia principală aa
      rows.push({
        REC: row.rec === 1 ? "✔️" : "",
        Jurnal: row.nr_jurnal_special || "—",
        SKU: row.sku || "—",
        Summary: row.summary || "—",
        Furnizor: row.nume_si_prenume || "—",
        "Data Intrare": formatDate(row.data_intrare),
        "Pret Intrare": formatNumber(row.pret_intrare),
        "Data Vanzare": formatDate(row.data_vandut),
        "Pret Vanzare": formatNumber(row.pret_de_vanzare_ron),
        Profit: formatNumber(row.profitRON),
        TVA: formatNumber(row.tvaRON),
        "Impozit Profit": formatNumber(row.REZULTAT_FORMULA),
        Client: clientName,
        Factura: facturaNr,
        Comanda: comandaId,
        "Data Factura": dataFactura,
        "Pret Luxura": pretLuxura,
      });

      // Linie de storno dacă existăb
      if (row.refundInfo) {
        rows.push({
          REC: "",
          Jurnal: row.nr_jurnal_special || "—",
          SKU: row.sku || "—",
          Summary: row.summary || "—",
          Furnizor: row.nume_si_prenume || "—",
          "Data Intrare": formatDate(row.data_intrare),
          "Pret Intrare": formatNumber(row.pret_intrare),
          "Data Vanzare": formatDate(row.data_vandut),
          "Pret Vanzare": `-${formatNumber(row.refundInfo.valoare_storno)}`,
          Profit: "0.00",
          TVA: "0.00",
          "Impozit Profit": "0.00",
          Client: clientName,
          Factura: row.refundInfo.storno_id || "—",
          Comanda: comandaId,
          "Data Factura": formatDate(row.refundInfo.data_cerere_storno),
          "Pret Luxura": pretLuxura,
        });
      }
    });

    const headers = Object.keys(rows[0]);

    const titleText = meta.title || "Jurnal Vanzari";
    const generatedText = meta.generatedAt
      ? `Generat la: ${meta.generatedAt}`
      : "";
    const filterText = meta.filterLabel ? `Filtru: ${meta.filterLabel}` : "";

    doc.setFontSize(14);
    doc.text(titleText, 14, 14);
    doc.setFontSize(9);
    if (generatedText) doc.text(generatedText, 14, 20);
    if (filterText) doc.text(filterText, 14, 24);

    doc.autoTable({
      head: [headers],
      body: rows.map((row) => headers.map((key) => row[key])),
      startY: 30,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 123, 255], textColor: 255 },
    });

    doc.save("Export_Vanzari.pdf");
  };

  const handleRefreshTasks = async () => {
    await fetchSalesData();
  };

  return (
    <>
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(135deg, #0f3443 0%, #34e89e 100%)",
          color: "common.white",
          boxShadow: "0px 25px 60px rgba(15, 23, 42, 0.3)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Panou Vânzări
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Înainte să creezi jurnalul de vânzări, verifică rapid dacă există
              erori în baza de date.
            </Typography>
            <Chip
              label={`Erori actuale: ${numarErori}`}
              color={numarErori > 0 ? "error" : "success"}
              sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.25)", color: "#fff" }}
            />
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            justifyContent="flex-end"
            width={{ xs: "100%", md: "auto" }}
          >
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => setShowRectificari(true)}
              sx={{ ...actionButtonStyles, bgcolor: "rgba(255,255,255,0.28)" }}
            >
              Rectificări
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() => exportToExcel(tableRows)}
              sx={actionButtonStyles}
            >
              Export Excel
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={() =>
                exportToPDF(tableRows, {
                  title: "Jurnal Vanzari",
                  generatedAt,
                  filterLabel: filteredRangeLabel,
                })
              }
              sx={actionButtonStyles}
            >
              Export PDF
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefreshTasks}
              sx={{ ...actionButtonStyles, bgcolor: "rgba(0,0,0,0.25)" }}
            >
              Reîncarcă
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h6">Filtrează rapid perioada</Typography>
              <Typography variant="body2" color="text.secondary">
                Alege un interval personalizat sau folosește un preset popular
                pentru a cunoaște performanța într-o perioadă precisă.
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={handleClearRange}
              disabled={!isRangeSelected}
            >
              Resetează filtrele
            </Button>
          </Stack>
          <Box sx={{ width: { xs: "100%", sm: 360 } }}>
            <DatePicker
              selectsRange
              startDate={salesStartDate}
              endDate={salesEndDate}
              onChange={handleRangeChange}
              dateFormat="dd MMM yyyy"
              shouldCloseOnSelect={false}
              customInput={
                <DateRangeInput placeholder="Selectează intervalul dorit" />
              }
            />
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {QUICK_RANGE_PRESETS.map((preset) => (
              <Chip
                key={preset.value}
                label={preset.label}
                onClick={() => handleQuickRangeSelect(preset.value)}
                color={
                  selectedQuickRange === preset.value ? "primary" : "default"
                }
                variant={
                  selectedQuickRange === preset.value ? "filled" : "outlined"
                }
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #f7971e, #ffd200)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(247, 151, 30, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Preț de Vânzare
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {formatKpiValue(summaryTotals.totalPretVanzare)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #ff512f, #dd2476)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(255, 81, 47, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <MonetizationOnIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Profit
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {formatKpiValue(summaryTotals.totalProfit)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #1cb5e0, #000851)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(28, 181, 224, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  TVA
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {formatKpiValue(summaryTotals.totalTVA)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #7f00ff, #e100ff)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(127, 0, 255, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Impozit pe Profit
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {formatKpiValue(summaryTotals.totalImpozit)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #11998e, #38ef7d)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(17, 153, 142, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <ExpandMoreIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Total Vânzări
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {integerCurrencyFormatter.format(totalVanzari)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  înregistrări
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabel Vânzări */}
      <Box sx={{ minWidth: 1100 }}>
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, boxShadow: 3, overflowX: "auto" }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#007BFF" }}>
                {TABLE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || "center"}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                      textAlign: column.align || "center",
                    }}
                  >
                    <TableSortLabel
                      active={sortConfig.field === column.id}
                      direction={
                        sortConfig.field === column.id
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, index) => {
                // Helper for missing values
                const missing = "—";
                // Formatters
                const formatDate = (d) =>
                  d ? new Date(d).toLocaleDateString() : missing;
                const formatNumber = (n) =>
                  n !== undefined && n !== null && !isNaN(Number(n))
                    ? Number(n).toFixed(2)
                    : missing;
                // OrderInfo helpers
                const clientName =
                  row.orderInfo && row.orderInfo.first_name
                    ? `${row.orderInfo.first_name} ${row.orderInfo.last_name}`
                    : missing;
                const facturaNr =
                  row.orderInfo && row.orderInfo._ywpi_invoice_formatted_number
                    ? row.orderInfo._ywpi_invoice_formatted_number
                    : missing;
                const comandaId =
                  row.orderInfo && row.orderInfo.ID
                    ? row.orderInfo.ID
                    : missing;
                const dataFactura =
                  row.orderInfo && row.orderInfo._ywpi_invoice_date
                    ? formatDate(row.orderInfo._ywpi_invoice_date)
                    : missing;
                const pretLuxura =
                  row.orderInfo && row.orderInfo.total
                    ? formatNumber(row.orderInfo.total)
                    : missing;
                return (
                  <React.Fragment key={index}>
                    <TableRow
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:nth-of-type(odd)": { backgroundColor: "#fcfcfc" },
                      }}
                    >
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        <Checkbox
                          checked={parseInt(row.rec) === 1}
                          onChange={async () => {
                            try {
                              const newRecValue = !(parseInt(row.rec) === 1);
                              const response = await axios.post(
                                "https://server-9p4hx.kinsta.app/api/jurnale/update-rec",
                                {
                                  issue_id: row.issue_id,
                                  rec: newRecValue ? 1 : 0,
                                }
                              );
                              if (response.status === 200) {
                                const updated = salesDataFiltered.map((item) =>
                                  item.issue_id === row.issue_id
                                    ? { ...item, rec: newRecValue ? 1 : 0 }
                                    : item
                                );
                                setSalesDataFiltered(updated);
                              }
                            } catch (error) {
                              console.error(
                                "Eroare la actualizarea REC în baza de date:",
                                error
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {row.nr_jurnal_special || missing}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {row.sku || missing}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                          cursor: "pointer",
                        }}
                        onClick={() => alert(row.summary || missing)}
                        title="Click pentru a vedea întregul summary"
                      >
                        {row.summary ? row.summary.slice(0, 5) : missing}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {row.nume_si_prenume || missing}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatDate(row.data_intrare)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatNumber(row.pret_intrare)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatDate(row.data_vandut)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatNumber(row.pret_de_vanzare_ron)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatNumber(row.profitRON)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {formatNumber(row.tvaRON)}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {row.REZULTAT_FORMULA !== undefined &&
                        row.REZULTAT_FORMULA !== null &&
                        !isNaN(Number(row.REZULTAT_FORMULA))
                          ? Number(row.REZULTAT_FORMULA).toFixed(2)
                          : missing}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {clientName}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {facturaNr}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {comandaId}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {dataFactura}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: "0.9rem",
                          padding: "8px",
                        }}
                      >
                        {pretLuxura}
                      </TableCell>
                    </TableRow>
                    {row.refundInfo && (
                      <TableRow
                        key={`${index}-refund`}
                        sx={{
                          backgroundColor: "#fafafa",
                          borderTop: "2px solid #e0e0e0",
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        />
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.nr_jurnal_special || missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.sku || missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => alert(row.summary || missing)}
                          title="Click pentru a vedea întregul summary"
                        >
                          {row.summary ? row.summary.slice(0, 5) : missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.nume_si_prenume || missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {formatDate(row.data_intrare)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {formatNumber(row.pret_intrare)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {formatDate(row.data_vandut)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.refundInfo.valoare_storno !== undefined &&
                          row.refundInfo.valoare_storno !== null &&
                          !isNaN(Number(row.refundInfo.valoare_storno))
                            ? `-${Number(row.refundInfo.valoare_storno).toFixed(2)}`
                            : missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          0.00
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          0.00
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          0.00
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {clientName}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.refundInfo.storno_id
                            ? row.refundInfo.storno_id
                            : missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {comandaId}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {row.refundInfo.data_cerere_storno
                            ? formatDate(row.refundInfo.data_cerere_storno)
                            : missing}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            fontSize: "0.9rem",
                            padding: "8px",
                          }}
                        >
                          {pretLuxura}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {showRectificari && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 1300,
            overflowY: "auto",
            padding: 4,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            onClick={() => setShowRectificari(false)}
            sx={{ mb: 2 }}
          >
            Închide
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Verificare SKUs
            </Typography>
            <FormControl>
              <InputLabel>An</InputLabel>
              <Select
                value={selectedYear}
                label="An"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                sx={{ minWidth: 120 }}
              >
                {[2022, 2023, 2024, 2025].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  SKU-uri vândute în JIRA dar lipsă în platformă
                </Typography>
                {!(
                  croscheckData &&
                  Array.isArray(croscheckData.vandutInJiraNotInVanzari)
                ) || croscheckData.vandutInJiraNotInVanzari.length === 0 ? (
                  <Typography color="text.secondary">
                    Nicio eroare detectată.
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {croscheckData.vandutInJiraNotInVanzari.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{ background: "#f9f9f9", p: 1.5, borderRadius: 1 }}
                      >
                        <Typography variant="body2">
                          <strong>
                            {item.sku && typeof item.sku === "string"
                              ? item.sku.trim()
                              : item.sku}
                          </strong>
                          : {item.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  SKU-uri din platformă dar fără status vândut în JIRA
                </Typography>
                {!(
                  croscheckData &&
                  Array.isArray(croscheckData.vandutInVanzariNotInJira)
                ) || croscheckData.vandutInVanzariNotInJira.length === 0 ? (
                  <Typography color="text.secondary">
                    Nicio eroare detectată.
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {croscheckData.vandutInVanzariNotInJira.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{ background: "#f9f9f9", p: 1.5, borderRadius: 1 }}
                      >
                        <Typography variant="body2">
                          <strong>
                            {item.sku && typeof item.sku === "string"
                              ? item.sku.trim()
                              : item.sku}
                          </strong>
                          : {item.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}

export default VanzariTab;
